import * as angular from 'angular';
import * as $ from 'jquery';
import * as moment from 'moment';
import * as _ from 'lodash';

import { ComponentDefinition, Interval, IApprovalStrategy, ApprovalSettings, LevelStatusWithDate, IApprovalButtonsConfiguration, Group, ISaveApprovalStatusDto, Dictionary, TimeUnit } from '../../../models';
import { IAuthorizationService, IDateRangeService, IStoreService, IGroupApprovalService, IAuthenticationService, IDateHelperFormatService } from '../../../services';
import { momentFormat, guidEmpty, formDirtyChanged, refreshClick } from '../../../common';
import { StoreElement } from '../../../stateManagement';

interface IApprovalStateParams {
    beginDate: string;
    groupGuid: string;
    approvalStrategyGuid: string;
}

class ApprovalStateController implements angular.IComponentController, angular.IOnInit, angular.IOnDestroy {
    approvalSettings: { [guid: string]: ApprovalSettings; };
    buttonsConfiguration: IApprovalButtonsConfiguration;
    canEdit: boolean = false;
    currentInterval: any;
    date: string;
    format: string;
    formController = { $dirty: false };
    globalLevel: number;
    globalResults: LevelStatusWithDate[];
    globalStatus: number;
    groupCtrl: any;
    groupDic: Dictionary<Group>;
    groups: Group[];
    interval: Interval;
    isLoading: boolean;
    preventFuture: boolean;
    selectedDate: string;
    selectedRow: any;
    selectedStrategy: IApprovalStrategy;
    strategies: IApprovalStrategy[];
    strategyDic: Dictionary<IApprovalStrategy>;
    treeCtrl: any;

    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$rootScope',
        '$state',
        '$stateParams',
        '$q',
        'storeService',
        'authorizationService',
        'dateRangeService',
        'groupApprovalService',
        'dateHelperFormatService'
    ];
    constructor(private _$scope: angular.IScope,
        private _$rootScope: angular.IRootScopeService,
        private _$state: angular.ui.IStateService,
        private _$stateParams: IApprovalStateParams,
        private _$q: angular.IQService,
        private _storeService: IStoreService,
        private _authorizationService: IAuthorizationService,
        private _dateRangeService: IDateRangeService,
        private _groupApprovalService: IGroupApprovalService,
        dateHelperFormatService: IDateHelperFormatService,
    ) {
        this.format = dateHelperFormatService.getDateFormat();
    }

    $onInit() {
        this.canEdit = this._authorizationService.canEdit();
        this.initDate();

        this._$scope.$on(refreshClick, (event, data) => {
            this.refresh(data ? data.force : true);
        });

        this._$scope.$watch(() => (this.groupCtrl && this.groupCtrl.isDirty)
            || (this.treeCtrl && this.treeCtrl.isDirty), newValue => {
                this._$rootScope.$broadcast(formDirtyChanged, newValue);
            });

        this._$scope.$watch(() => this.date, () => {
            const endDate = moment(this.date)
                .add(this.interval.interval, this.interval.unit)
                .toISOString();
            this.currentInterval = { startDate: this.date, endDate: endDate };
        });

        this.preventFuture = false;

        this._unsubscribe = this._storeService.register(store => {
            this.strategyDic = store.approvalStrategies.dictionary;
            this.strategies = _.values<IApprovalStrategy>(store.approvalStrategies.dictionary)
                .filter(s => s.guid !== guidEmpty);
            this.approvalSettings = store.approvalSettings.dictionary;
            this.groupDic = store.groups.dictionary;
            this.groups = _.values<Group>(store.groups.dictionary);
            if (this.strategies.length) {
                if (this._$stateParams['approvalStrategyGuid']) {
                    this.select(store.approvalStrategies.dictionary[this._$stateParams['approvalStrategyGuid']]);
                } else {
                    this.select(this.strategies[0]);
                }
            }
        }, [StoreElement.ApprovalStrategies, StoreElement.ApprovalSettings, StoreElement.Groups]);
    }

    $onDestroy(): void {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    approveAll() {
        this.isLoading = true;
        const dto: ISaveApprovalStatusDto[] = this.groups.filter(g => g.approvalStrategyGuid === this.selectedStrategy.guid)
            .map(g => {
                const res: ISaveApprovalStatusDto = {
                    beginDate: this._dateRangeService.beginDate() as string,
                    comment: '',
                    endDate: this._dateRangeService.endDate() as string,
                    groupGuid: g.guid,
                    level: this.globalLevel,
                    status: this.globalStatus
                };
                return res;
            });
        this._groupApprovalService.saveStatuses(dto)
            .then(data => {
                this._$rootScope.$broadcast(refreshClick);
            })
            .finally(() => this.isLoading = false);
    }

    navigateConfig() {
        this._$state.go('approvalConfiguration', {
            returnParams: {
                beginDate: this._dateRangeService.beginDate() as string,
                groupGuid: this.selectedRow ? this.selectedRow.group.guid : undefined,
                approvalStrategyGuid: this.selectedStrategy.guid
            },
            approvalStrategyGuid: this.selectedStrategy.guid
        });
    }

    select(strategy: IApprovalStrategy) {
        this.selectedStrategy = strategy;
        this.refresh();
    }

    private refresh(force?: boolean) {
        if (this.selectedStrategy && this.approvalSettings[this.selectedStrategy.guid].globalApprovalLevels.length > 0) {
            //Always refresh
            this._groupApprovalService.getGlobalStatuses(this._dateRangeService.beginDate() as string, this._dateRangeService.endDate() as string, this.selectedStrategy.guid, true)
                .then(data => {
                    const globalApprovalLevels = this.approvalSettings[this.selectedStrategy.guid].globalApprovalLevels;
                    const allowedLevels = this.selectedStrategy.approbationStrategyLevelCollection.map(d => d.level)
                        .filter(l => globalApprovalLevels.indexOf(l) >= 0 && data.currentUserLevels.indexOf(l) >= 0);
                    data.levelStatusWithDates.forEach(lswd => {
                        let message = null;
                        // if (allowedLevels.indexOf(lswd.level) < 0) {
                        //     message = 'You cannot approve at this level.';
                        // }
                        if (globalApprovalLevels.indexOf(lswd.level) < 0) {
                            message = 'You cannot approve/reject globally at this level';
                        }
                        if (message) {
                            lswd.manualComment = message;
                        }
                    });
                    this.buttonsConfiguration = {
                        allowFuture: this.selectedStrategy.allowInFuture,
                        approvals: data.levelStatusWithDates,
                        approve: (level, status, dateToApprove) => {
                            this.globalLevel = level;
                            this.globalStatus = status;
                            $('#approvaAllModal').modal('show');
                            return this._$q.when();
                        },
                        currentUserLevels: allowedLevels,
                        interval: null,
                        levelCount: this.selectedStrategy.approbationStrategyLevelCollection.length,
                        timestamp: this._dateRangeService.endDate() as Date,
                        hideIcon: true
                    };
                });
        }
    }

    private initDate() {
        this.interval = { interval: 1, unit: 'M' };
        const referenceDate = this._$stateParams.beginDate
            ? new Date(this._$stateParams.beginDate)
            : new Date();
        const beginMoment = moment(referenceDate).startOf('month');
        this.date = beginMoment.format(momentFormat);
        if (this._$stateParams.beginDate) {
            this.selectedDate = moment(this._$stateParams.beginDate).startOf('d').toISOString();
        }
    }
}

export const wtApprovalState: ComponentDefinition = {
    componentId: 'wtApprovalState',
    controller: ApprovalStateController,
    template: require('./approvalState.component.html'),
    controllerAs: 'vm',
    bindings: {

    }
};